// Discover — two decks that feed one taste profile.
//
//   ingredients   like / neutral / dislike   builds the profile
//   dishes        dislike / meh / looks good / know & like   spends it
//
// Ingredients come first by design: the profile is what generates the dish
// deck, so an unrated profile means an empty, meaningless dish deck. The
// ingredient card shows *why* it surfaced ("bridges mushroom, soy sauce") so
// the loop reads as reasoning rather than magic.

import { useRef, useState } from "react";
import useSWR from "swr";
import { Check, Clock, Heart, Meh, Minus, Sparkles, X } from "lucide-react";

import { fetcher, imageUrl, post } from "@/api/client";
import { cn } from "@/lib/utils";
import { playClick, playSwitch } from "@/sounds";

type Track = "ingredient" | "dish";

interface IngredientCard {
  name: string;
  display: string;
  image: string | null;
  image_source: "themealdb" | "wikipedia" | "none" | null;
  image_credit: string | null;
  primary_category: string | null;
  food_group: string | null;
  cuisine_region: string | null;
  is_vegan: number;
  reason: string | null;
  score: number | null;
  confidence: number | null;
}

interface DishCard {
  id: number;
  name: string;
  image: string | null;
  ingredients: { name: string; quantity: string | null }[];
  steps_rough: string | null;
  description: string | null;
  total_min: number | null;
  servings: number | null;
  cuisine: string | null;
  tags: string;
  source: string;
  predicted_score: number | null;
}

interface IngredientStats {
  total: number;
  rated: number;
  liked: number;
  neutral: number;
  disliked: number;
  queued: number;
  phase: "calibration" | "explore";
  calibration_target: number;
}

interface DishStats {
  total: number;
  rated: number;
  liked: number;
  library: number;
  remaining_by_source: Record<string, number>;
}

const SWIPE_THRESHOLD = 90;

export default function Discover() {
  const [track, setTrack] = useState<Track>("ingredient");
  return (
    <div className="flex flex-1 flex-col items-center gap-4 overflow-y-auto p-4 md:p-6">
      <TrackToggle track={track} onChange={setTrack} />
      {track === "ingredient" ? <IngredientDeck /> : <DishDeck />}
    </div>
  );
}

function TrackToggle({ track, onChange }: { track: Track; onChange: (t: Track) => void }) {
  return (
    <div className="flex w-full max-w-xl justify-center">
      <div className="flex rounded-full border border-line-strong p-1">
        {(["ingredient", "dish"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              onChange(t);
              playClick();
            }}
            className={cn(
              "cursor-pointer rounded-full px-5 py-1.5 text-xs font-semibold transition-colors",
              track === t ? "bg-brand text-primary-foreground" : "text-dim hover:text-foreground",
            )}
          >
            {t === "ingredient" ? "ingredients" : "dishes"}
          </button>
        ))}
      </div>
    </div>
  );
}

/** Shared swipe handling: drag right = positive, left = negative.
 *
 * The commit callback is supplied to `bind()` at render time rather than to
 * the hook, so the component can define its verdict handler after the hook
 * (the handler needs `reset`, and the hook would otherwise need the handler).
 */
function useSwipe() {
  const [drag, setDrag] = useState(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef<number | null>(null);

  const stop = () => {
    start.current = null;
    setDragging(false);
  };

  return {
    drag,
    dragging,
    reset: () => setDrag(0),
    bind: (onCommit: (positive: boolean) => void) => ({
      onPointerDown: (e: React.PointerEvent) => {
        start.current = e.clientX;
        setDragging(true);
      },
      onPointerMove: (e: React.PointerEvent) => {
        if (start.current !== null) setDrag(e.clientX - start.current);
      },
      onPointerUp: () => {
        if (Math.abs(drag) > SWIPE_THRESHOLD) onCommit(drag > 0);
        else setDrag(0);
        stop();
      },
      onPointerCancel: () => {
        setDrag(0);
        stop();
      },
    }),
  };
}

// ── Ingredients ───────────────────────────────────────────────────────────

function IngredientDeck() {
  const { data: cards, mutate } = useSWR<IngredientCard[]>(
    "/api/ingredients/next?limit=20",
    fetcher,
    { revalidateOnFocus: false },
  );
  const { data: stats, mutate: mutateStats } = useSWR<IngredientStats>(
    "/api/ingredients/stats",
    fetcher,
  );

  const deck = cards ?? [];
  const card = deck[0];

  const swipe = useSwipe();

  async function judge(verdict: "like" | "neutral" | "dislike") {
    if (!card) return;
    playSwitch(verdict === "like");
    swipe.reset();
    void mutate(deck.slice(1), { revalidate: deck.length <= 3 });
    void mutateStats();
    await post(`/api/ingredients/${encodeURIComponent(card.name)}/verdict`, { verdict });
  }

  const calibrating = stats?.phase === "calibration";
  const progress = stats
    ? calibrating
      ? Math.min(1, stats.rated / stats.calibration_target)
      : 1
    : 0;

  if (!card) {
    return (
      <EmptyDeck
        title={stats?.rated ? "all caught up" : "no vocabulary loaded"}
        body={
          stats?.rated
            ? "Every queued ingredient is rated. New suggestions arrive as the profile settles — or force a refresh."
            : "Run scripts/load_vocab.py to load the ingredient vocabulary."
        }
        action={stats?.rated ? { label: "more suggestions", href: "/api/ingredients/requeue" } : undefined}
        onAction={async () => {
          await post("/api/ingredients/requeue", {});
          void mutate();
          void mutateStats();
        }}
      />
    );
  }

  return (
    <>
      <div className="w-full max-w-xl">
        <div className="mb-1 flex items-baseline justify-between text-xs text-dim">
          <span className="font-semibold">
            {calibrating ? "calibrating your palate" : "learning what you like"}
          </span>
          <span className="font-mono">
            {stats?.rated ?? 0}
            {calibrating ? ` / ${stats?.calibration_target ?? 40}` : ""} rated
            {stats ? ` · ♥ ${stats.liked}` : ""}
          </span>
        </div>
        <div className="h-1 overflow-hidden rounded-full bg-[var(--fill-track)]">
          <div
            className="h-full rounded-full bg-brand transition-[width] duration-500 ease-epicure"
            style={{ width: `${progress * 100}%` }}
          />
        </div>
      </div>

      <div
        className="relative w-full max-w-xl touch-pan-y select-none"
        {...swipe.bind((positive) => void judge(positive ? "like" : "dislike"))}
      >
        <div
          style={{
            transform: `translateX(${swipe.drag}px) rotate(${swipe.drag / 30}deg)`,
            transition: swipe.dragging ? "none" : "transform 400ms var(--ease-epicure)",
          }}
          className="overflow-hidden rounded-[var(--radius-card)] border border-line-strong bg-surface p-8 shadow-[0_15px_40px_rgba(0,0,0,0.2)]"
        >
          {imageUrl(card.image) && (
            // Cut-outs (TheMealDB, transparent PNG) sit on the panel colour;
            // photos (Wikipedia) fill the frame.
            <div className="-mx-8 -mt-8 mb-6 flex h-56 items-center justify-center bg-panel md:h-64">
              <img
                src={imageUrl(card.image)!}
                alt=""
                draggable={false}
                className={cn(
                  "h-full w-full",
                  card.image_source === "themealdb"
                    ? "object-contain p-6 drop-shadow-[0_12px_24px_rgba(0,0,0,0.25)]"
                    : "object-cover",
                )}
              />
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 text-xs text-dim">
            {card.primary_category && (
              <span className="rounded-full border border-line px-2.5 py-0.5">
                {card.primary_category}
              </span>
            )}
            {card.cuisine_region && card.cuisine_region !== "universal" && (
              <span className="rounded-full border border-line px-2.5 py-0.5">
                {card.cuisine_region.replace(/_/g, " ")}
              </span>
            )}
            {card.is_vegan === 1 && (
              <span className="rounded-full border border-line px-2.5 py-0.5">vegan</span>
            )}
          </div>

          <h2 className="mt-5 font-display text-5xl leading-[0.95] font-medium tracking-[-0.03em] text-balance">
            {card.display}
          </h2>

          {card.reason && (
            <p className="mt-5 flex items-start gap-2 text-sm text-brand-ink">
              <Sparkles className="mt-0.5 size-3.5 shrink-0" />
              <span>{card.reason}</span>
            </p>
          )}
          {card.reason === "calibration" && (
            <p className="mt-5 text-sm text-dim">
              Spreading the net wide — these first cards map the whole flavour space.
            </p>
          )}
          {card.image && card.image_source === "wikipedia" && (
            <p className="mt-4 text-[10px] text-dimmer">
              photo · Wikipedia{card.image_credit && card.image_credit !== card.display ? ` (${card.image_credit})` : ""}
            </p>
          )}
        </div>

        <SwipeHint drag={swipe.drag} positive="like" negative="nope" />
      </div>

      <div className="flex items-center gap-3 pb-2">
        <RoundButton label="Dislike" tone="error" onClick={() => void judge("dislike")}>
          <X className="size-6" />
        </RoundButton>
        <PillButton label="Neutral" onClick={() => void judge("neutral")}>
          <Minus className="size-4" /> neutral
        </PillButton>
        <RoundButton label="Like" tone="brand" onClick={() => void judge("like")}>
          <Heart className="size-6" />
        </RoundButton>
      </div>
    </>
  );
}

// ── Dishes ────────────────────────────────────────────────────────────────

type DishVerdict = "dislike" | "meh" | "good" | "know_like";

function DishDeck() {
  const { data: cards, mutate } = useSWR<DishCard[]>("/api/candidates/next?limit=20", fetcher, {
    revalidateOnFocus: false,
  });
  const { data: stats, mutate: mutateStats } = useSWR<DishStats>("/api/candidates/stats", fetcher);
  const [showSteps, setShowSteps] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const deck = cards ?? [];
  const card = deck[0];

  const swipe = useSwipe();

  async function judge(verdict: DishVerdict) {
    if (!card) return;
    playSwitch(verdict === "good" || verdict === "know_like");
    swipe.reset();
    setShowSteps(false);
    void mutate(deck.slice(1), { revalidate: deck.length <= 3 });
    void mutateStats();
    await post(`/api/candidates/${card.id}/verdict`, { verdict });
  }

  async function refresh() {
    setRefreshing(true);
    try {
      await post("/api/candidates/refresh", {});
      void mutate();
      void mutateStats();
    } finally {
      setRefreshing(false);
    }
  }

  if (!card) {
    return (
      <EmptyDeck
        title={refreshing ? "pulling a batch…" : "deck is dry"}
        body={
          refreshing
            ? "Querying Epicure, TheMealDB and based.cooking with your liked ingredients."
            : "Rate ingredients first — the dish deck is built from your taste profile, so it means nothing until the profile does."
        }
        onAction={refresh}
        action={{ label: "pull 100 candidates", href: "#" }}
      />
    );
  }

  return (
    <>
      <div className="flex w-full max-w-xl items-center justify-between text-xs text-dim">
        <span className="font-semibold">{card.source}</span>
        <span className="font-mono">
          {stats?.rated ?? 0} rated · {stats?.library ?? 0} in library
          {card.predicted_score != null && ` · match ${Math.round(card.predicted_score * 100)}%`}
        </span>
      </div>

      <div
        className="relative w-full max-w-xl touch-pan-y select-none"
        {...swipe.bind((positive) => void judge(positive ? "good" : "dislike"))}
      >
        <div
          style={{
            transform: `translateX(${swipe.drag}px) rotate(${swipe.drag / 30}deg)`,
            transition: swipe.dragging ? "none" : "transform 400ms var(--ease-epicure)",
          }}
          className="overflow-hidden rounded-[var(--radius-card)] border border-line-strong bg-surface shadow-[0_15px_40px_rgba(0,0,0,0.2)]"
        >
          {imageUrl(card.image) ? (
            <img
              src={imageUrl(card.image)!}
              alt=""
              draggable={false}
              className="aspect-[4/3] w-full object-cover"
            />
          ) : (
            <div className="flex aspect-[4/3] items-center justify-center font-display text-8xl italic text-dimmer">
              {card.name.slice(0, 1)}
            </div>
          )}
          <div className="p-5">
            <h2 className="font-display text-3xl leading-tight font-medium tracking-[-0.02em] text-balance">
              {card.name}
            </h2>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-dim">
              {card.total_min != null && (
                <span className="flex items-center gap-1">
                  <Clock className="size-3" /> {card.total_min} min
                </span>
              )}
              {card.servings != null && <span>{card.servings} servings</span>}
              {card.cuisine && <span>{card.cuisine}</span>}
              {card.tags && <span className="italic">{card.tags}</span>}
            </div>
            {card.description && (
              <p className="mt-3 text-sm leading-relaxed text-dim">{card.description}</p>
            )}
            {card.ingredients.length > 0 && (
              <p className="mt-3 text-sm leading-relaxed text-dim">
                {card.ingredients
                  .slice(0, 8)
                  .map((i) => i.name)
                  .join(" · ")}
                {card.ingredients.length > 8 && " · …"}
              </p>
            )}
            {card.steps_rough && (
              <button
                type="button"
                onClick={() => setShowSteps((s) => !s)}
                className="mt-2 cursor-pointer text-xs font-semibold text-brand-ink"
              >
                {showSteps ? "hide method" : "method"}
              </button>
            )}
            {showSteps && card.steps_rough && (
              <p className="mt-2 max-h-40 overflow-y-auto text-xs leading-relaxed whitespace-pre-line text-dim">
                {card.steps_rough}
              </p>
            )}
          </div>
        </div>

        <SwipeHint drag={swipe.drag} positive="looks good" negative="nope" />
      </div>

      <div className="flex flex-wrap items-center justify-center gap-2 pb-2">
        <RoundButton label="Dislike" tone="error" onClick={() => void judge("dislike")}>
          <X className="size-6" />
        </RoundButton>
        <PillButton label="Meh" onClick={() => void judge("meh")}>
          <Meh className="size-4" /> meh
        </PillButton>
        <PillButton label="Looks good" onClick={() => void judge("good")}>
          <Heart className="size-4" /> looks good
        </PillButton>
        <RoundButton label="Know and like" tone="brand" onClick={() => void judge("know_like")}>
          <Check className="size-6" />
        </RoundButton>
      </div>
      <p className="pb-2 text-center text-xs text-dimmer">
        “looks good” and “know &amp; like” both add the dish to your library
      </p>
    </>
  );
}

// ── Shared chrome ─────────────────────────────────────────────────────────

function SwipeHint({
  drag,
  positive,
  negative,
}: {
  drag: number;
  positive: string;
  negative: string;
}) {
  if (drag === 0) return null;
  return (
    <div
      className={cn(
        "pointer-events-none absolute top-6 rounded-full px-4 py-1.5 text-sm font-bold",
        drag > 0
          ? "left-6 bg-brand text-primary-foreground"
          : "right-6 bg-[var(--status-error-tint)] text-status-error",
      )}
    >
      {drag > 0 ? positive : negative}
    </div>
  );
}

function RoundButton({
  label,
  tone,
  onClick,
  children,
}: {
  label: string;
  tone: "brand" | "error";
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className={cn(
        "flex size-14 cursor-pointer items-center justify-center rounded-full transition-transform duration-300 ease-epicure hover:-translate-y-0.5 active:scale-95",
        tone === "brand"
          ? "bg-brand text-primary-foreground"
          : "border border-line-strong text-status-error",
      )}
    >
      {children}
    </button>
  );
}

function PillButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="flex h-11 cursor-pointer items-center gap-1.5 rounded-full border border-line-strong px-4 text-xs font-semibold text-dim transition-transform duration-300 ease-epicure hover:-translate-y-0.5 hover:text-foreground active:scale-95"
    >
      {children}
    </button>
  );
}

function EmptyDeck({
  title,
  body,
  action,
  onAction,
}: {
  title: string;
  body: string;
  action?: { label: string; href: string };
  onAction?: () => void | Promise<void>;
}) {
  return (
    <div className="flex flex-1 items-center justify-center text-center">
      <div>
        <p className="font-display text-4xl italic text-dim">{title}</p>
        <p className="mx-auto mt-3 max-w-sm text-sm text-dim">{body}</p>
        {action && onAction && (
          <button
            type="button"
            onClick={() => void onAction()}
            className="mt-6 cursor-pointer rounded-full bg-brand px-5 py-2.5 text-xs font-semibold text-primary-foreground transition-transform duration-300 ease-epicure hover:-translate-y-0.5 active:scale-95"
          >
            {action.label}
          </button>
        )}
      </div>
    </div>
  );
}
